import Router from 'next/router'
import { Constants } from 'common/utils/constants'
import { Project } from 'common/project'
import { ApiTypes } from 'common/api/types/api-types'
import { setApi } from 'common/api'
import storage from './async-storage-api'
import Strings, { makeStrings } from './localisation'
import { Crisp } from 'crisp-sdk-web'
import { fromParam } from '@hoxtonmix/utils'
import flagsmith from '@flagsmith/flagsmith/isomorphic'

declare const dataLayer: { push: (data: Record<string, unknown>) => void }
declare const mixpanel: { identify: (id: string) => void }

export type GAEventType = 'page_view' | 'event'
export type GAEvent =
  | 'add_to_cart_ga4'
  | 'remove_from_cart_ga4'
  | 'purchase_ga4'
  | 'view_item_ga4'

export interface GA4Item {
  item_id: string
  item_name: string
  price: string
  currency: 'GBP'
  affiliation?: string
  coupon?: string
  discount?: number
  index?: number
  item_brand?: string
  item_category?: string
  item_category2?: string
  item_category3?: string
  item_category4?: string
  item_category5?: string
  item_list_id?: string
  item_list_name?: string
  item_variant?: string
  location_id?: string
  quantity?: number
  promotion_id?: string
  promotion_name?: string
}

export interface GA4Purchase {
  currency: 'GBP'
  transaction_id: string
  value: string
  items: GA4Item[]
  coupon?: string
  affiliation?: string
  tax?: string
  shipping?: string
}

export interface GA4AddToCart {
  currency: 'GBP'
  transaction_id: string
  value: string
  items: GA4Item[]
}

export type GAEventData = GA4Purchase | GA4AddToCart

interface WebStorageExtension {
  removeItem: (key: string, options?: any) => undefined | unknown
  getItemSync: (key: string, options?: any) => string
  setItemSync: (
    key: string,
    value: string,
    options?: any,
  ) => undefined | unknown
  removeItemSync: (key: string, options?: any) => undefined | unknown
}

interface WebAPI extends ApiTypes<any, WebStorageExtension> {
  getStoredLocale: (requestedLocale?: string) => string
  setStoredLocale: (requestedLocale: string) => void
  logoutRedirect: () => void
  setProperties: (data: Record<string, unknown>, signature?: string) => void
  setToken: (token: string) => void
}

const gtag = (
  eventType: GAEventType,
  event?: GAEvent,
  data?: GAEventData,
): void => {
  if (eventType === 'page_view') {
    const payload = {
      event: 'Pageview',
      pagePath: document.location.href,
      pageTitle: document.title,
      visitorType: 'customer',
    }
    API.log('ANALYTICS', payload)
    dataLayer.push(payload)
  } else {
    const payload = {
      ecommerce_ga4: data,
      event,
    }
    API.log('ANALYTICS', payload)
    dataLayer.push(payload)
  }
}

const API: WebAPI = {
  confirmEmailRedirect: (email) => {
    Router.replace(`/auth/confirm-email?email=${email}`)
  },

  getAPIBaseUrl: () => process.env.NEXT_PUBLIC_API_URL || '',

  getFlagsmith: () => flagsmith,

  getStoredLocale: (requestedLocale?: string) => {
    return (
      API.storage.getItemSync('NEXT_LOCALE') ||
      requestedLocale ||
      Constants.defaultLocale
    )
  },

  getToken: () => {
    if (document.location.pathname === '/verification') {
      return fromParam().t
    }
    return null
  },

  identify: (id) => {
    if (Project.mixpanel && typeof mixpanel !== 'undefined') {
      mixpanel.identify(id)
    }
  },

  isMobile: () => false,

  log: (namespace, ...args) => {
    if (Project.logs[namespace]) {
      // eslint-disable-next-line no-console
      console.log.apply(null, [namespace, ...args])
    }
  },

  loginRedirect: (_user, _defaultSubscriptionId) => {
    const params = Router.query
    params.redirect = params.redirect || '/'
    const redirect = params.redirect as string
    const as = (params.as as string) || redirect
    Router.replace(redirect, as, { shallow: true })
  },

  logout: () => {
    Router.replace('/')
  },

  logoutRedirect: () => {
    if (Router.query.t) {
      return
    }
    if (document.location.pathname === '/') {
      return
    }
    const search = document.location.search
      ? `&params=${document.location.search}`
      : ''
    const path = `/?redirect=${encodeURIComponent(
      `${document.location.pathname}${search}`,
    )}`
    Router.replace(path)
  },

  makeStrings,

  middlewares: [],

  platform: 'web',

  prefetchMailItemFiles: () => Promise.resolve(),

  setProperties: (data, signature) => {
    if (data) {
      if (signature) {
        Crisp.setTokenId(signature)
      }
      if (data.name) {
        Crisp.user.setNickname(data.name as string)
      }
      if (data.user_id) {
        Crisp.user.setEmail(data.user_id as string, signature)
      }
      Crisp.session.setData(data)
    }
  },

  setStoredLocale: (locale) => {
    Strings.setLanguage(locale)
    API.storage.setItemSync('NEXT_LOCALE', locale)
    const { asPath, pathname, query } = Router
    Router.push({ pathname, query }, asPath, { locale })
  },

  setToken: () => {
    // Token management handled by storage
  },

  storage,

  trackEvent: (event) => {
    if (typeof dataLayer !== 'undefined') {
      gtag('event', event.event as GAEvent, event as unknown as GAEventData)
    }
  },

  trackPage: () => {
    if (typeof dataLayer !== 'undefined') {
      gtag('page_view')
    }
  },

  utm_source: 'customer_portal',
}

setApi(API)
export { API }
